import AsyncStorage from '@react-native-async-storage/async-storage';
import type { AnalyzedItem } from './gemini';

const PENDING_APPRAISALS_KEY = 'pending_appraisals';

export interface PendingAppraisal {
  item: AnalyzedItem;
  imageUri: string;
  imageBase64?: string; // Store base64 for persistence across sessions
  timestamp: number;
}

/**
 * Save an appraisal result for later (when user signs in)
 * @param imageBase64 - Optional base64 image data for persistence
 */
export async function savePendingAppraisal(
  item: AnalyzedItem, 
  imageUri: string,
  imageBase64?: string
): Promise<void> {
  try {
    const existing = await getPendingAppraisals();
    const newAppraisal: PendingAppraisal = {
      item,
      imageUri,
      imageBase64,
      timestamp: Date.now(),
    };
    
    // Keep max 5 pending appraisals (with images, storage is larger)
    const updated = [...existing, newAppraisal].slice(-5);
    await AsyncStorage.setItem(PENDING_APPRAISALS_KEY, JSON.stringify(updated));
    console.log('[PendingAppraisals] Saved appraisal for later:', item.name, imageBase64 ? '(with image)' : '(no image)');
  } catch (error) {
    console.error('[PendingAppraisals] Failed to save:', error);
  }
}

/**
 * Get all pending appraisals
 */
export async function getPendingAppraisals(): Promise<PendingAppraisal[]> {
  try {
    const data = await AsyncStorage.getItem(PENDING_APPRAISALS_KEY);
    if (!data) return [];
    
    const appraisals = JSON.parse(data) as PendingAppraisal[];
    
    // Filter out appraisals older than 7 days
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    const filteredAppraisals = appraisals.filter(a => a.timestamp > sevenDaysAgo);
    
    // Persist the filtered list so expired entries are removed from storage
    if (filteredAppraisals.length === 0) {
      await AsyncStorage.removeItem(PENDING_APPRAISALS_KEY);
    } else if (filteredAppraisals.length !== appraisals.length) {
      await AsyncStorage.setItem(PENDING_APPRAISALS_KEY, JSON.stringify(filteredAppraisals));
      console.log('[PendingAppraisals] Cleaned up expired entries:', appraisals.length - filteredAppraisals.length);
    }
    
    return filteredAppraisals;
  } catch (error) {
    console.error('[PendingAppraisals] Failed to get:', error);
    return [];
  }
}

/**
 * Check if there are any pending appraisals
 */
export async function hasPendingAppraisals(): Promise<boolean> {
  const appraisals = await getPendingAppraisals();
  return appraisals.length > 0;
}

/**
 * Clear all pending appraisals (after user adds them to Cache)
 */
export async function clearPendingAppraisals(): Promise<void> {
  try {
    await AsyncStorage.removeItem(PENDING_APPRAISALS_KEY);
    console.log('[PendingAppraisals] Cleared all pending appraisals');
  } catch (error) {
    console.error('[PendingAppraisals] Failed to clear:', error);
  }
}

/**
 * Remove a specific pending appraisal by index
 */
export async function removePendingAppraisal(index: number): Promise<void> {
  try {
    const existing = await getPendingAppraisals();
    existing.splice(index, 1);
    await AsyncStorage.setItem(PENDING_APPRAISALS_KEY, JSON.stringify(existing));
  } catch (error) {
    console.error('[PendingAppraisals] Failed to remove:', error);
  }
}
